import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Preset, PresetGroup } from '../lib/supabase';
import { formatFoodRelation } from '../lib/scheduler';

interface SupplementSelectorProps {
  presets: Preset[];
  groups: (PresetGroup & { presets: Preset[] })[];
  selectedPresetId?: string;
  selectedGroupId?: string;
  onChange: (presetId: string, groupId: string) => void;
  label?: string;
  placeholder?: string;
}

export function SupplementSelector({
  presets,
  groups,
  selectedPresetId = '',
  selectedGroupId = '',
  onChange,
  label = 'Select Supplement or Group',
  placeholder = 'Search supplements...',
}: SupplementSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getSelectedName = (): string => {
    if (selectedGroupId) {
      const group = groups.find((g) => g.id === selectedGroupId);
      return group ? `📦 ${group.name}` : '';
    }
    if (selectedPresetId) {
      const preset = presets.find((p) => p.id === selectedPresetId);
      return preset ? preset.name : '';
    }
    return '';
  };

  const handleSelect = (presetId: string, groupId: string) => {
    console.log('SupplementSelector handleSelect called:', { presetId, groupId });
    onChange(presetId, groupId);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('', '');
    setSearchTerm('');
    setIsOpen(false);
  };

  const filteredGroups = groups.filter((g) =>
    g.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPresets = presets.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasSelection = selectedPresetId || selectedGroupId;
  const selectedName = getSelectedName();

  console.log('SupplementSelector render:', {
    presetsCount: presets.length,
    groupsCount: groups.length,
    filteredPresetsCount: filteredPresets.length,
    filteredGroupsCount: filteredGroups.length,
    searchTerm,
    selectedPresetId,
    selectedGroupId,
  });

  return (
    <div ref={containerRef}>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        {hasSelection && (
          <button
            onClick={handleClear}
            type="button"
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>

      {hasSelection && (
        <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-900">
          Selected: <span className="font-medium">{selectedName}</span>
        </div>
      )}

      <div className="relative mb-2">
        <Search
          size={16}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {isOpen && (
        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
        {filteredGroups.length === 0 && filteredPresets.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            {searchTerm ? 'No results found' : 'No supplements available'}
          </div>
        ) : (
          <>
            {filteredGroups.length > 0 && (
              <div>
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0">
                  Groups
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredGroups.map((group) => {
                    const isSelected = group.id === selectedGroupId;
                    return (
                      <div
                        key={group.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Group clicked:', group.name);
                          handleSelect('', group.id);
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors cursor-pointer ${
                          isSelected ? 'bg-blue-100' : ''
                        }`}
                      >
                        <div className="font-medium text-gray-900">📦 {group.name}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {group.presets.length} supplement{group.presets.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {filteredPresets.length > 0 && (
              <div>
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0">
                  Individual Supplements
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredPresets.map((preset) => {
                    const isSelected = preset.id === selectedPresetId;
                    return (
                      <div
                        key={preset.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Preset clicked:', preset.name);
                          handleSelect(preset.id, '');
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors cursor-pointer ${
                          isSelected ? 'bg-blue-100' : ''
                        }`}
                      >
                        <div className="font-medium text-gray-900">{preset.name}</div>
                        <div className="flex gap-2 mt-1 text-xs flex-wrap">
                          <span className="text-gray-600">{preset.tag}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-600">Priority {preset.priority}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-600">
                            {formatFoodRelation(preset.food_relation)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
        </div>
      )}
    </div>
  );
}
